// One cargo, one row: its identity and measurements, the six cost cells the
// four column groups are made of, and the frozen total the row adds up to.
import { useTranslation } from 'react-i18next';
import {
  LOGISTICS_CALCULATION_SHEET_CHROME,
  type LogisticsCalculationCurrency,
} from '@magimp/shared';
import {
  DocButton,
  DocDate,
  DocText,
  useDocumentEditing,
  type DocumentPath,
} from './DocumentCell.js';
import { Amounts, CostCell } from './DocumentCost.js';
import { ExtrasCell } from './DocumentExtras.js';
import {
  DOCUMENT_VOLUME_UNIT,
  DOCUMENT_WEIGHT_UNIT,
  cargoDensity,
  formatCargoDayMonth,
  measureWithUnit,
  nonZeroAmounts,
  settlementLine,
  type DocumentRates,
} from './document-format.js';
import type { Cargo, CargoRowAmounts, SingleCostField } from './sheet-amounts.js';

const doc = LOGISTICS_CALCULATION_SHEET_CHROME.document;
const COLUMNS = doc.tableColumnHeaders;
const ARROW = '↓';

/** Which column each single-value cost cell prints under, and in what currency. */
const COST_COLUMNS: ReadonlyArray<{
  field: SingleCostField;
  column: string;
  currency: LogisticsCalculationCurrency;
  captioned: boolean;
  /** i18n key of the example the empty caption line shows. */
  hint?: 'transport' | 'insurance';
}> = [
  { field: 'transport', column: COLUMNS[5], currency: 'USD', captioned: true, hint: 'transport' },
  { field: 'insurance', column: COLUMNS[6], currency: 'USD', captioned: true, hint: 'insurance' },
  { field: 'packaging', column: COLUMNS[7], currency: 'USD', captioned: false },
  { field: 'prr', column: COLUMNS[9], currency: 'RUB', captioned: false },
  { field: 'clientDelivery', column: COLUMNS[10], currency: 'RUB', captioned: false },
];

export function DocumentCargoRow({
  index,
  cargo,
  amounts,
  rates,
  onRemove,
}: {
  index: number;
  cargo: Cargo | null;
  amounts: CargoRowAmounts;
  rates: DocumentRates | null;
  onRemove?: (() => void) | undefined;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const path = `cargos.${index}`;
  const cell = (field: SingleCostField) => COST_COLUMNS.find((entry) => entry.field === field)!;
  const costCell = (field: SingleCostField) => {
    const { column, currency, captioned, hint } = cell(field);
    return (
      <td
        className="doc-r doc-n"
        data-cost={field}
        data-section-start={field === 'transport' || field === 'prr' ? '' : undefined}
      >
        <CostCell
          name={`${path}.${field}` as DocumentPath}
          column={column}
          defaultCurrency={currency}
          cost={amounts.costs[field]}
          captioned={captioned}
          hint={hint === undefined ? undefined : t(`editor.cost.hints.${hint}`)}
        />
      </td>
    );
  };
  return (
    <tr data-testid="rl-document-cargo-row">
      <td className="doc-c doc-muted doc-n doc-w-no doc-pin">{index + 1}</td>
      <td className="doc-idw doc-pin doc-pin-end">
        <div className="doc-art doc-n">
          <DocText
            name={`${path}.article` as DocumentPath}
            label={t('editor.cargo.article')}
            value={cargo?.article ?? ''}
          />
        </div>
        <div className="doc-nm">
          <DocText
            name={`${path}.name` as DocumentPath}
            label={t('editor.cargo.name')}
            value={cargo?.name ?? ''}
          />
          {onRemove ? (
            <DocButton label={t('editor.cargo.remove')} danger onClick={onRemove} />
          ) : null}
        </div>
      </td>
      <td className="doc-road">
        <DocText
          name={`${path}.transportType` as DocumentPath}
          label={t('editor.cargo.transportType')}
          value={cargo?.transportType ?? ''}
        />
      </td>
      <td className="doc-c doc-n">
        <DocText
          name={`${path}.seats` as DocumentPath}
          label={t('editor.cargo.seats')}
          value={cargo?.seats ?? ''}
          numeric
        />
      </td>
      <MeasuresCell index={index} cargo={cargo} />
      <DatesCell index={index} cargo={cargo} />
      {costCell('transport')}
      {costCell('insurance')}
      {costCell('packaging')}
      <td className="doc-r doc-n" data-cost="extras">
        <ExtrasCell cargoIndex={index} column={COLUMNS[8]} amounts={amounts} />
      </td>
      {costCell('prr')}
      {costCell('clientDelivery')}
      <TotalCell cargo={cargo} rates={rates} />
    </tr>
  );
}

function MeasuresCell({ index, cargo }: { index: number; cargo: Cargo | null }) {
  const { t } = useTranslation('logisticsCalculations');
  const editing = useDocumentEditing();
  const density = cargoDensity(cargo?.weight, cargo?.volume);
  return (
    <td className="doc-r doc-n doc-metr">
      <span className="doc-measure">
        <DocText
          name={`cargos.${index}.weight` as DocumentPath}
          label={t('editor.cargo.weight')}
          value={measureWithUnit(cargo?.weight, DOCUMENT_WEIGHT_UNIT) ?? ''}
          numeric
        />
        {editing ? ` ${DOCUMENT_WEIGHT_UNIT}` : null}
      </span>
      <span className="doc-sm">
        <DocText
          name={`cargos.${index}.volume` as DocumentPath}
          label={t('editor.cargo.volume')}
          value={measureWithUnit(cargo?.volume, DOCUMENT_VOLUME_UNIT) ?? ''}
          numeric
        />
        {editing ? ` ${DOCUMENT_VOLUME_UNIT}` : null}
        {density ? <span className="doc-density">{density}</span> : null}
      </span>
    </td>
  );
}

function DatesCell({ index, cargo }: { index: number; cargo: Cargo | null }) {
  const { t } = useTranslation('logisticsCalculations');
  const editing = useDocumentEditing();
  const departed = formatCargoDayMonth(cargo?.departedAt);
  const arrived = formatCargoDayMonth(cargo?.arrivedAt);
  return (
    <td className="doc-c doc-n doc-dates">
      <DocDate
        name={`cargos.${index}.departedAt` as DocumentPath}
        label={t('editor.cargo.departedAt')}
        value={departed ?? ''}
      />
      {/* The arrow joins two dates. With neither stated it would be the only
          mark in the cell, reading as a leg the document does not describe. */}
      {editing || departed || arrived ? <span className="doc-arr">{ARROW}</span> : null}
      <DocDate
        name={`cargos.${index}.arrivedAt` as DocumentPath}
        label={t('editor.cargo.arrivedAt')}
        value={arrived ?? ''}
      />
    </td>
  );
}

/**
 * The frozen per-currency total of the row, one currency per printed line, and
 * under it what those come to in the currencies the customer pays.
 */
function TotalCell({ cargo, rates }: { cargo: Cargo | null; rates: DocumentRates | null }) {
  const settlement = settlementLine(cargo?.totals, rates);
  return (
    <td className="doc-r doc-n doc-tot" data-total="" data-section-start="">
      <Amounts amounts={nonZeroAmounts(cargo?.totals)} bold />
      {settlement ? <span className="doc-eq">{settlement}</span> : null}
    </td>
  );
}
